import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { FunnelTableItemDto } from 'api/funnel';
import { CheckboxComponent as Checkbox } from 'components/forms/Checkbox';
import { NsKey } from 'enums/NsKey';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { contractsAPI, funnelsAPI } from 'services/swagger-api';
import { getMyTunnelsAvailableForCreation } from '../api/myTunnelsApi';

const COLLECTIVE_TUNNEL_TYPE = 2;

export interface ProductsSelectorHandle {
  /** Sauvegarde la sélection (diff add/remove). Throw si erreur. */
  save: () => Promise<void>;
}

interface ProductsSelectorProps {
  groupContractId: string;
  /** Notifie le parent quand la validité de la sélection change (active/désactive le bouton Save). */
  onValidityChange?: (isValid: boolean) => void;
  /**
   * Si true, ne propose que les tunnels où le courtier courant est habilité
   * (table CourtierTunnelHabilitation). Mode "création par courtier".
   * Si false (défaut), propose tous les tunnels collectifs de la plateforme
   * (mode admin/orga).
   */
  restrictToMyHabilitations?: boolean;
}

interface ProductGroup {
  id: string;
  label: string;
  tunnels: FunnelTableItemDto[];
}

export const ProductsSelector = forwardRef<ProductsSelectorHandle, ProductsSelectorProps>(
  ({ groupContractId, onValidityChange, restrictToMyHabilitations = false }, ref) => {
    const { t } = useDynamicTranslation([NsKey.Contract, NsKey.Common]);

    const [tunnels, setTunnels] = useState<FunnelTableItemDto[]>([]);
    const [plateformeId, setPlateformeId] = useState<string | null>(null);
    const [persistedActiveTunnelIds, setPersistedActiveTunnelIds] = useState<string[]>([]);
    /**
     * Tunnel sélectionné par produit. Présence dans la map = produit sélectionné.
     * Valeur = '' tant qu'un tunnel n'est pas choisi (cas multi-tunnels).
     */
    const [selectedTunnelByProduct, setSelectedTunnelByProduct] = useState<Record<string, string>>({});
    const [hasInitializedSelection, setHasInitializedSelection] = useState(false);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);

    // Charge le contrat (orga + tunnels actifs).
    useEffect(() => {
      let cancelled = false;
      (async () => {
        try {
          const contract = await contractsAPI.getGroupContractByID({ groupContractId });
          if (cancelled) return;
          if (contract.plateformeId) setPlateformeId(contract.plateformeId);
          setPersistedActiveTunnelIds(
            (contract.tunnels ?? []).filter(tn => tn.isActive).map(tn => tn.tunnelId),
          );
        } catch (err) {
          console.error('Failed to load contract for products selector', err);
        }
      })();
      return () => { cancelled = true; };
    }, [groupContractId]);

    // Charge les tunnels collectifs de la plateforme.
    // Si `restrictToMyHabilitations`, intersecte avec la liste des tunnels où
    // le courtier courant est habilité (CourtierTunnelHabilitation).
    useEffect(() => {
      if (!plateformeId) return;
      let cancelled = false;
      setLoading(true);
      (async () => {
        try {
          const [funnels, myHabilitations] = await Promise.all([
            funnelsAPI.getFunnels({
              getGenericListCommandDto: {
                pageNumber: 1,
                pageSize: 100,
                searchString: '',
                filters: { plateformeIds: [plateformeId], typeTunnel: COLLECTIVE_TUNNEL_TYPE },
              },
            }),
            restrictToMyHabilitations
              ? getMyTunnelsAvailableForCreation()
              : Promise.resolve(null),
          ]);

          if (cancelled) return;

          let items = funnels.items ?? [];
          if (restrictToMyHabilitations && myHabilitations) {
            const allowed = new Set(myHabilitations.tunnelIds);
            items = items.filter((tn) => allowed.has(tn.id));
          }
          setTunnels(items);
        } catch (err) {
          if (!cancelled) {
            console.error('Failed to load tunnels', err);
            setTunnels([]);
          }
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
      return () => { cancelled = true; };
    }, [plateformeId, restrictToMyHabilitations]);

    // Une fois la liste complète des tunnels chargée → initialise la sélection en partant de l'existant.
    useEffect(() => {
      if (hasInitializedSelection || tunnels.length === 0) return;
      const tunnelById = new Map(tunnels.map(tn => [tn.id, tn]));
      const initial: Record<string, string> = {};
      for (const tunnelId of persistedActiveTunnelIds) {
        const tn = tunnelById.get(tunnelId);
        const productId = tn?.product?.id;
        if (productId) initial[productId] = tunnelId;
      }
      setSelectedTunnelByProduct(initial);
      setHasInitializedSelection(true);
    }, [tunnels, persistedActiveTunnelIds, hasInitializedSelection]);

    // Groupe les tunnels par produit.
    const products = useMemo<ProductGroup[]>(() => {
      const byProduct = new Map<string, ProductGroup>();
      for (const tn of tunnels) {
        const productId = tn.product?.id;
        if (!productId) continue;
        let group = byProduct.get(productId);
        if (!group) {
          group = { id: productId, label: tn.product?.label ?? '', tunnels: [] };
          byProduct.set(productId, group);
        }
        group.tunnels.push(tn);
      }
      return Array.from(byProduct.values()).sort((a, b) => a.label.localeCompare(b.label));
    }, [tunnels]);

    const filtered = useMemo(() => {
      if (!search) return products;
      const q = search.toLowerCase();
      return products.filter(p => p.label.toLowerCase().includes(q));
    }, [products, search]);

    const selectedCount = Object.keys(selectedTunnelByProduct).length;
    const hasIncompleteSelection = Object.values(selectedTunnelByProduct).some(v => !v);
    const isValid = selectedCount > 0 && !hasIncompleteSelection;

    // Notifie le parent à chaque changement de validité.
    useEffect(() => {
      onValidityChange?.(isValid);
    }, [isValid, onValidityChange]);

    const toggleProduct = (group: ProductGroup) => {
      setSelectedTunnelByProduct(prev => {
        const next = { ...prev };
        if (group.id in next) {
          delete next[group.id];
        } else {
          next[group.id] = group.tunnels.length === 1 ? group.tunnels[0].id : '';
        }
        return next;
      });
    };

    const selectTunnel = (productId: string, tunnelId: string) => {
      setSelectedTunnelByProduct(prev => ({ ...prev, [productId]: tunnelId }));
    };

    // Expose la save via ref impérative.
    useImperativeHandle(ref, () => ({
      async save() {
        const contract = await contractsAPI.getGroupContractByID({ groupContractId });
        const persistedActive = (contract.tunnels ?? [])
          .filter(tn => tn.isActive)
          .map(tn => tn.tunnelId);
        const persistedSet = new Set(persistedActive);
        const desiredTunnelIds = Object.values(selectedTunnelByProduct).filter(Boolean);
        const desiredSet = new Set(desiredTunnelIds);

        const toAdd = desiredTunnelIds.filter(id => !persistedSet.has(id));
        const toRemove = persistedActive.filter(id => !desiredSet.has(id));

        for (const id of toAdd) {
          await contractsAPI.addTunnelToGroupContract({
            groupContractId,
            addTunnelToGroupContractCommand: { tunnelId: id },
          });
        }
        for (const id of toRemove) {
          await contractsAPI.removeTunnelFromGroupContract({ groupContractId, tunnelId: id });
        }
      },
    }), [groupContractId, selectedTunnelByProduct]);

    return (
      <div className="flex flex-col gap-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('GROUP_CONTRACT_PROCESS.PRODUCTS_STEP.PLACEHOLDER')}
            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
          />
        </div>

        {loading && (
          <div className="text-sm text-gray-500">
            {t('GROUP_CONTRACT_PROCESS.PRODUCTS_STEP.LOADING')}
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="text-sm text-gray-500 text-center py-8">
            {t('GROUP_CONTRACT_PROCESS.PRODUCTS_STEP.EMPTY')}
          </div>
        )}

        <div className="flex flex-col gap-2 max-h-96 overflow-y-auto">
          {filtered.map(product => {
            const isSelected = product.id in selectedTunnelByProduct;
            const selectedTunnelId = selectedTunnelByProduct[product.id];
            const hasMultipleTunnels = product.tunnels.length > 1;

            return (
              <div
                key={product.id}
                className={`flex flex-col gap-3 p-3 border rounded-lg transition ${
                  isSelected ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-start gap-3">
                  <Checkbox
                    name={`product-${product.id}`}
                    checked={isSelected}
                    onChange={() => toggleProduct(product)}
                  />
                  {/* onClick uniquement sur le texte (pas sur la case) : évite le double
                      déclenchement onChange + onClick qui annulait la sélection sur Edge. */}
                  <div
                    className="flex-1 cursor-pointer"
                    onClick={() => toggleProduct(product)}
                  >
                    <div className="font-medium">{product.label}</div>
                    {hasMultipleTunnels && (
                      <div className="text-xs text-gray-500">
                        {t('GROUP_CONTRACT_PROCESS.PRODUCTS_STEP.MULTI_TUNNEL_HINT', {
                          count: product.tunnels.length,
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {isSelected && hasMultipleTunnels && (
                  <div className="ml-9 flex flex-col gap-1.5 border-l-2 border-primary/30 pl-3">
                    <div className="text-xs font-medium text-gray-700">
                      {t('GROUP_CONTRACT_PROCESS.PRODUCTS_STEP.SELECT_TUNNEL')}
                    </div>
                    {product.tunnels.map(tn => {
                      const radioId = `tunnel-${tn.id}`;
                      return (
                        <label
                          key={tn.id}
                          htmlFor={radioId}
                          className="flex items-center gap-2 text-sm cursor-pointer hover:text-primary"
                        >
                          <input
                            id={radioId}
                            type="radio"
                            name={`tunnel-radio-${product.id}`}
                            checked={selectedTunnelId === tn.id}
                            onChange={() => selectTunnel(product.id, tn.id)}
                            className="text-primary focus:ring-primary"
                          />
                          <span className={selectedTunnelId === tn.id ? 'font-medium' : 'text-gray-700'}>
                            {tn.label}
                          </span>
                          <span className="text-xs text-gray-500">({tn.code})</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="text-sm text-gray-600">
          {t('GROUP_CONTRACT_PROCESS.PRODUCTS_STEP.COUNT', { count: selectedCount })}
        </div>

        {hasIncompleteSelection && (
          <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            {t('GROUP_CONTRACT_PROCESS.PRODUCTS_STEP.INCOMPLETE_WARNING')}
          </div>
        )}
      </div>
    );
  },
);

ProductsSelector.displayName = 'ProductsSelector';
